import { Injectable } from '@angular/core';

const AGENT_URL = 'http://127.0.0.1:5055';
const PRINTER_STORAGE_KEY = 'print-agent-printer';

export type AgentPrinterStatus =
  | 'ready'
  | 'busy'
  | 'printing'
  | 'offline'
  | 'paperOut'
  | 'paperJam'
  | 'paused'
  | 'error'
  | 'unknown';

export type AgentJobStatus =
  | 'queued'
  | 'spooling'
  | 'printing'
  | 'done'
  | 'error'
  | 'cancelled'
  | 'unknown';

export interface IAgentHealth {
  ok: boolean;
  version: string | null;
  tokenRequired: boolean;
}

export interface IAgentPrinter {
  name: string;
  isDefault: boolean;
  isNetwork: boolean;
  status: AgentPrinterStatus;
  statusText: string | null;
  jobCount: number;
}

export interface IAgentPrinters {
  default: string | null;
  printers: IAgentPrinter[];
}

export interface IAgentJob {
  jobId: string;
  printer: string | null;
  status: AgentJobStatus;
  position: number | null;
  pagesPrinted: number | null;
  totalPages: number | null;
  error: string | null;
}

const PRINTER_STATUSES: AgentPrinterStatus[] = [
  'ready',
  'busy',
  'printing',
  'offline',
  'paperOut',
  'paperJam',
  'paused',
  'error',
];

const JOB_STATUSES: AgentJobStatus[] = [
  'queued',
  'spooling',
  'printing',
  'done',
  'error',
  'cancelled',
];

const BLOCKING_PRINTER_STATUSES: AgentPrinterStatus[] = [
  'offline',
  'paperOut',
  'paperJam',
  'paused',
  'error',
];

export function isPrinterBlocked(
  printer: IAgentPrinter | null | undefined,
): boolean {
  return !printer || BLOCKING_PRINTER_STATUSES.includes(printer.status);
}

export function isPrinterBusy(
  printer: IAgentPrinter | null | undefined,
): boolean {
  return (
    !!printer &&
    !isPrinterBlocked(printer) &&
    (printer.status === 'busy' ||
      printer.status === 'printing' ||
      printer.jobCount > 0)
  );
}

export function isJobFinished(status: AgentJobStatus): boolean {
  return (
    status === 'done' ||
    status === 'error' ||
    status === 'cancelled' ||
    status === 'unknown'
  );
}

/** Agentga umuman ulanib bo'lmadi: o'rnatilmagan, yopiq yoki CORS bloklagan. */
export class PrintAgentUnavailableError extends Error {
  constructor() {
    super('print_agent_unavailable');
  }
}

/** Agent javob berdi, lekin xato bilan; `message` agentning o'z matni. */
export class PrintAgentRequestError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

@Injectable({ providedIn: 'root' })
export class PrintAgentService {
  async isAvailable(): Promise<boolean> {
    return (await this.getHealth()) !== null;
  }

  async getHealth(): Promise<IAgentHealth | null> {
    try {
      const res = await fetch(`${AGENT_URL}/health`, {
        signal: AbortSignal.timeout(1500),
      });
      if (!res.ok) {
        return null;
      }
      const data = await res.json();
      return {
        ok: !!data?.ok,
        version: typeof data?.version === 'string' ? data.version : null,
        tokenRequired: !!data?.tokenRequired,
      };
    } catch {
      return null;
    }
  }

  async getPrinters(): Promise<IAgentPrinters> {
    const data = await this.request('GET', '/printers', undefined, 4000);
    const defaultName: string | null =
      typeof data?.default === 'string' && data.default ? data.default : null;
    const raw: unknown[] = Array.isArray(data?.printers) ? data.printers : [];

    return {
      default: defaultName,
      printers: raw
        .map((item) => this.normalizePrinter(item, defaultName))
        .filter((p): p is IAgentPrinter => p !== null),
    };
  }

  async getJob(jobId: string): Promise<IAgentJob> {
    const data = await this.request(
      'GET',
      `/jobs/${encodeURIComponent(jobId)}`,
    );
    return this.normalizeJob(data, jobId);
  }

  async cancelJob(jobId: string): Promise<void> {
    await this.request('DELETE', `/jobs/${encodeURIComponent(jobId)}`);
  }

  async print(pdf: Blob, printer?: string | null): Promise<IAgentJob> {
    const params = new URLSearchParams();
    if (printer) {
      params.set('printer', printer);
    }
    const query = params.toString();

    const data = await this.request(
      'POST',
      `/print${query ? `?${query}` : ''}`,
      pdf,
    );
    return this.normalizeJob(data, '');
  }

  getSavedPrinter(): string | null {
    try {
      return localStorage.getItem(PRINTER_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  savePrinter(name: string | null): void {
    try {
      if (name) {
        localStorage.setItem(PRINTER_STORAGE_KEY, name);
      } else {
        localStorage.removeItem(PRINTER_STORAGE_KEY);
      }
    } catch {
      /* storage mavjud emas */
    }
  }

  private async request(
    method: 'GET' | 'POST' | 'DELETE',
    path: string,
    body?: Blob,
    timeoutMs?: number,
  ): Promise<any> {
    let res: Response;
    try {
      res = await fetch(`${AGENT_URL}${path}`, {
        method,
        headers: body ? { 'Content-Type': 'application/pdf' } : undefined,
        body,
        signal: timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined,
      });
    } catch {
      throw new PrintAgentUnavailableError();
    }

    const text = await res.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }

    if (!res.ok) {
      const message =
        (typeof data?.error === 'string' && data.error) ||
        (typeof data?.message === 'string' && data.message) ||
        '';
      throw new PrintAgentRequestError(res.status, message);
    }
    return data;
  }

  /** Eski agent faqat nomlar massivini qaytaradi, yangisi obyektlar; ikkalasi ham qabul qilinadi. */
  private normalizePrinter(
    item: unknown,
    defaultName: string | null,
  ): IAgentPrinter | null {
    if (typeof item === 'string') {
      return item
        ? {
            name: item,
            isDefault: item === defaultName,
            isNetwork: false,
            status: 'unknown',
            statusText: null,
            jobCount: 0,
          }
        : null;
    }

    const p = item as Record<string, any> | null;
    if (!p || typeof p['name'] !== 'string' || !p['name']) {
      return null;
    }
    const status = PRINTER_STATUSES.includes(p['status'])
      ? p['status']
      : 'unknown';
    return {
      name: p['name'],
      isDefault: p['isDefault'] === true || p['name'] === defaultName,
      isNetwork: p['isNetwork'] === true,
      status,
      statusText: typeof p['statusText'] === 'string' ? p['statusText'] : null,
      jobCount: Number(p['jobCount']) > 0 ? Number(p['jobCount']) : 0,
    };
  }

  private normalizeJob(data: any, fallbackId: string): IAgentJob {
    const toNumber = (v: unknown) =>
      v === null || v === undefined || Number.isNaN(Number(v))
        ? null
        : Number(v);

    return {
      jobId: data?.jobId != null ? String(data.jobId) : fallbackId,
      printer: typeof data?.printer === 'string' ? data.printer : null,
      status: JOB_STATUSES.includes(data?.status) ? data.status : 'unknown',
      position: toNumber(data?.position),
      pagesPrinted: toNumber(data?.pagesPrinted),
      totalPages: toNumber(data?.totalPages),
      error: typeof data?.error === 'string' && data.error ? data.error : null,
    };
  }
}
